import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CertificateDetail, VerificationResult } from '../../models/verify-signature-response.model';

export interface VerifyState {
  // Archivo a verificar
  file: File | null;

  // Resultados de verificación
  signatures: CertificateDetail[];
  selectedSignature: CertificateDetail | null;
  selectedIndex: number | null;

  // Resultado general
  verificationResult: VerificationResult | null;
  
  // Estados
  isProcessing: boolean;
  isVerified: boolean;
  autoVerify: boolean; // Flag para ejecutar verificación automáticamente
  error: string | null;
}

const initialState: VerifyState = {
  file: null,
  signatures: [],
  selectedSignature: null,
  selectedIndex: null,
  verificationResult: null,
  isProcessing: false,
  isVerified: false,
  autoVerify: false,
  error: null
};

@Injectable({
  providedIn: 'root'
})
export class VerifyStateService {
  private state = new BehaviorSubject<VerifyState>(initialState);
  
  /**
   * Observable del estado completo
   */
  public state$: Observable<VerifyState> = this.state.asObservable();

  /**
   * Obtiene el estado actual de forma síncrona
   */
  get currentState(): VerifyState {
    return this.state.value;
  }

  // ==========================================
  // ACCIONES DE ARCHIVO
  // ==========================================

  /**
   * Establece el archivo a verificar
   * @param file Archivo a verificar
   * @param autoVerify Si es true, se ejecutará la verificación automáticamente
   */
  setFile(file: File | null, autoVerify: boolean = false): void {
    this.state.next({
      ...this.currentState,
      file,
      autoVerify,
      // Limpiar resultados anteriores al cambiar archivo
      signatures: [],
      selectedSignature: null,
      selectedIndex: null,
      verificationResult: null,
      isVerified: false,
      error: null
    });
  }

  // ==========================================
  // ACCIONES DE RESULTADOS
  // ==========================================

  /**
   * Establece las firmas encontradas
   */
  setSignatures(signatures: CertificateDetail[]): void {
    this.state.next({
      ...this.currentState,
      signatures,
      // Sin selección inicial: ítems del acordeón quedan colapsados
      selectedSignature: null,
      selectedIndex: null,
      isVerified: signatures.length > 0
    });
  }

  /**
   * Selecciona una firma específica
   */
  selectSignature(signature: CertificateDetail | null, index: number | null): void {
    this.state.next({
      ...this.currentState,
      selectedSignature: signature,
      selectedIndex: index
    });
  }

  /**
   * Establece el resultado completo de verificación
   */
  setVerificationResult(result: VerificationResult | null): void {
    this.state.next({
      ...this.currentState,
      verificationResult: result,
      isVerified: true
    });
  }

  // ==========================================
  // ACCIONES DE ESTADOS
  // ==========================================

  /**
   * Establece el estado de procesamiento
   */
  setProcessing(isProcessing: boolean): void {
    this.state.next({
      ...this.currentState,
      isProcessing
    });
  }

  /**
   * Establece un error
   */
  setError(error: string | null): void {
    this.state.next({
      ...this.currentState,
      error,
      isProcessing: false
    });
  }

  // ==========================================
  // ACCIONES GLOBALES
  // ==========================================

  /**
   * Resetea el estado completo
   */
  reset(): void {
    this.state.next(initialState);
  }

  /**
   * Resetea solo los resultados manteniendo el archivo
   */
  resetResults(): void {
    this.state.next({
      ...this.currentState,
      signatures: [],
      selectedSignature: null,
      selectedIndex: null,
      verificationResult: null,
      isVerified: false,
      autoVerify: false,
      error: null
    });
  }

  /**
   * Resetea el flag autoVerify
   */
  resetAutoVerify(): void {
    this.state.next({
      ...this.currentState,
      autoVerify: false
    });
  }
}

